import React from 'react';
import { motion } from 'framer-motion';

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="about-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-none mb-6" data-testid="about-title">
              Marco Antoima
            </h1>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Artist Statement
            </p>
          </div>

          {/* Artist Image */}
          <div className="mb-16">
            <div className="relative w-full max-w-3xl mx-auto">
              <img
                src="https://customer-assets.emergentagent.com/job_canvas-market-19/artifacts/hq7h59gj_Screenshot%202026-02-05%20at%207.55.05%E2%80%AFPM.png"
                alt="Marco Antoima"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>

          {/* Artist Statement */}
          <div className="max-w-3xl mx-auto space-y-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-base md:text-lg leading-relaxed"
              data-testid="statement-paragraph-1"
            >
              My work emerges from an attentiveness to material, movement, and presence. I paint 
              primarily on natural wood, often cut into uneven or irregular forms, allowing the 
              surface itself to guide the composition. The wood is not neutral; it holds grain, 
              history, resistance. The shape becomes a boundary and a collaborator rather than a 
              container.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-base md:text-lg leading-relaxed"
              data-testid="statement-paragraph-2"
            >
              Through fluid paint, layered pours, and gravity-driven movement, the paintings evolve 
              through a balance of control and surrender. The forms that appear are not planned images, 
              but traces of process: erosion, emergence, pressure, and flow. They echo landscapes, 
              bodies, and inner terrains without naming them directly. What matters is not representation, 
              but sensation.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-base md:text-lg leading-relaxed"
              data-testid="statement-paragraph-3"
            >
              My photography moves through a parallel devotion rather than documentation. Where painting 
              is an inward ritual, photography unfolds through trust, intimacy, and consent. Both practices 
              are grounded in reverence: for the body, for material, and for moments of quiet charge. 
              Whether abstract or figurative, the work invites a slowed gaze and a felt response.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-base md:text-lg leading-relaxed"
              data-testid="statement-paragraph-4"
            >
              Across mediums, I am drawn to thresholds—places where one state becomes another. 
              These works are not meant to be consumed quickly, but approached, lingered with, and 
              entered. They function less as declarations and more as offerings, sites where attention 
              itself becomes the subject.
            </motion.p>
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-20 text-center border-t border-border pt-12"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Get in Touch
            </p>
            <p className="text-base md:text-lg">
              For inquiries about commissions or available works
            </p>
            <a
              href="mailto:hello@moderngallery.art"
              className="text-accent hover:underline text-base md:text-lg"
              data-testid="contact-email"
            >
              hello@moderngallery.art
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
